import {useCallback, useEffect, useState} from 'react';
import {Box, TextField} from '@mui/material';
import PropTypes from 'prop-types';
import RequiredCheckbox from './Shared/RequiredCheckbox';
import RichEditor from './Shared/RichEditor';
import RichEditorResultText from './Shared/RichEditorResultText';

const NumericInputBox = ({
                             index,
                             componentId,
                             priority,
                             onChange,
                             descriptionHtml,
                             onDescriptionHtmlChange,
                             commitDescriptionHtml,
                             isEditMode,
                             enableValidation,
                             isFormArchived,
                             readonly,
                             placeholderValues,
                             accessLevel,
                             submitMode,
                             error,
                             showEditModeOnly = false,
                             fieldKey = '',
                             value,
                             audience,
                             recipientType
                         }) => {
    const [draftDescriptionHtml, setDraftDescriptionHtml] = useState(descriptionHtml ?? '');
    const [isEditorFocused, setIsEditorFocused] = useState(false);

    const [inputValue, setInputValue] = useState('');

    useEffect(() => {
        if (isEditorFocused) return;
        setDraftDescriptionHtml(descriptionHtml ?? '');
    }, [descriptionHtml, isEditorFocused]);

    useEffect(() => {
        const next = priority ?? value ?? '';
        setInputValue(next === '' ? '' : next.toString());
    }, [priority, value]);

    const handleDraftDescriptionChange = useCallback((nextHtml) => {
        setDraftDescriptionHtml(nextHtml);
        onDescriptionHtmlChange(nextHtml);
    }, [onDescriptionHtmlChange]);

    const handleEditorBlur = useCallback(() => {
        setIsEditorFocused(false);
        commitDescriptionHtml();
    }, [commitDescriptionHtml]);

    const handleFocus = useCallback(() => {
        setIsEditorFocused(true);
    }, []);

    const handleNumericChange = (e) => {
        const raw = e.target.value;
        setInputValue(raw);

        const num = raw === '' ? '' : Number(raw);

        if (isEditMode) {
            onChange({
                index,
                priority: num
            });
        } else {
            onChange({
                id: componentId,
                value: num
            });
        }
    };

    if (showEditModeOnly && submitMode) return null;

    const stop = (e) => {
        e.stopPropagation();
    };

    return (
        <Box key={`NumericInputBox-${componentId}`} sx={{mt: 2}}>
            {isEditMode
                ? (
                    <Box sx={{mb: 1}}>
                        <RichEditor
                            index={index}
                            isEditMode
                            isFormLive={isFormArchived}
                            accessLevel={accessLevel}
                            currentDescription={draftDescriptionHtml}
                            setCurrentDescription={handleDraftDescriptionChange}
                            audience={audience}
                            recipientType={recipientType}
                            onFocus={handleFocus}
                            onBlur={handleEditorBlur}
                        />
                    </Box>
                )
                : (
                    <RichEditorResultText
                        id={componentId}
                        description={descriptionHtml}
                        placeholderValues={placeholderValues}
                        readonly={readonly}
                        enableValidation={enableValidation}
                        audience={audience}
                        recipientType={recipientType}
                        submitMode={submitMode}
                    />
                )}

            {!(isEditMode && !showEditModeOnly) &&
                ((!readonly && ((showEditModeOnly && isEditMode) || submitMode))
                        ? (
                        <TextField
                            fullWidth
                            required={enableValidation && !showEditModeOnly}
                            type="number"
                            value={inputValue}
                            onChange={handleNumericChange}
                            disabled={!submitMode && !isEditMode}
                            error={(fieldKey && !!error) || error}
                            helperText={error}
                            inputProps={{
                                step: 'any',
                                inputMode: 'numeric',
                                autoComplete: 'off',
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'e' || e.key === 'E') e.preventDefault();
                            }}
                            onPaste={(e) => {
                                const text = (e.clipboardData || window.clipboardData).getData('text');
                                if (/[eE.]/.test(text)) e.preventDefault();
                            }}
                            onMouseDown={stop}
                            onClick={stop}
                            onDoubleClick={stop}
                            onPointerDown={stop}
                            onFocus={stop}
                            sx={{mt: 1}}
                        />
                    )
                        : (
                        <TextField
                            fullWidth
                            value={inputValue}
                            InputProps={{readOnly: true}}
                            disabled
                            sx={{
                                mt: 1,
                                '& .MuiInputBase-input.Mui-disabled': {
                                    WebkitTextFillColor: 'black',
                                    color: 'black',
                                },
                            }}
                        />
                    )
            )}

            {isEditMode && (
                <Box sx={{mt: 1}}>
                    <RequiredCheckbox
                        enableValidation={fieldKey ? true : enableValidation}
                        index={index}
                        onChange={
                            fieldKey
                                ? () => {
                                }
                                : (showEditModeOnly
                                    ? () => {
                                    }
                                    : onChange)
                        }
                        isFormArchived={isFormArchived || fieldKey !== ''}
                        checked={fieldKey ? true : undefined}
                        disabled={fieldKey ? true : undefined}
                    />
                </Box>
            )}
        </Box>
    );
};

NumericInputBox.propTypes = {
    componentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    priority: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    index: PropTypes.number,
    descriptionHtml: PropTypes.string,
    onDescriptionHtmlChange: PropTypes.func.isRequired,
    commitDescriptionHtml: PropTypes.func.isRequired,
    enableValidation: PropTypes.bool,
    onChange: PropTypes.func,
    isEditMode: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    readonly: PropTypes.bool,
    placeholderValues: PropTypes.object,
    accessLevel: PropTypes.number,
    submitMode: PropTypes.bool,
    error: PropTypes.string,
    showEditModeOnly: PropTypes.bool,
    fieldKey: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    audience: PropTypes.number,
    recipientType: PropTypes.string
};

export default NumericInputBox;
